// ─────────────────────────────────────────────────────────────────────────────
// AOTD gallery filtering — the "only mine" view.
//
// The gallery listing (`GET /api/art-of-the-day/all/info`) is public and
// returns every listable day in one payload, so the filter is client-side:
// no extra request, no new endpoint, and the same data backs both views.
// Kept in its own module so the profile deep-link and the gallery agree on
// the query param, and so the matching rule is unit-testable on its own.
// ─────────────────────────────────────────────────────────────────────────────

/** Query param that puts the gallery in "only mine" mode. */
export const MINE_PARAM = "mine";

/** Value written when the filter is on (`?mine=1`). */
export const MINE_ON = "1";

export const GALLERY_PATH = "/art-of-the-day/gallery";

/** Deep link used by the profile button: the gallery, already filtered. */
export const MINE_GALLERY_URL = `${GALLERY_PATH}?${MINE_PARAM}=${MINE_ON}`;

/** Reads the param loosely so hand-typed `?mine=true` works too. */
export function isMineParam(value: string | null | undefined): boolean {
  if (!value) return false;
  const v = value.trim().toLowerCase();
  return v === MINE_ON || v === "true" || v === "yes";
}

type Authored = { createdBy?: string | null };

/**
 * An AOTD is yours when its `createdBy` equals your username exactly — the
 * same comparison the backend uses for author checks (commentsService), and
 * the same string the card prints as the author.
 *
 * Deliberately case-sensitive: backend usernames are unique but not
 * case-folded, so a loose match could claim someone else's day. Two known
 * limits, both pre-existing and site-wide:
 *   • a day claimed through a clique still counts as yours (`createdBy` stays
 *     your username), even though the card credits the clique;
 *   • AOTDs authored before a username change keep the old string, so they
 *     stay out of this view — exactly as they stay out of every other
 *     by-author surface.
 */
export function isAuthoredBy(
  art: Authored | null | undefined,
  username: string | null | undefined,
): boolean {
  if (!art || !username) return false;
  const createdBy = art.createdBy;
  if (typeof createdBy !== "string") return false;
  const mine = username.trim();
  if (!mine) return false;
  return createdBy.trim() === mine;
}

/** The subset of `items` authored by `username`; empty when nobody is signed in. */
export function filterMine<T extends Authored>(
  items: T[] | null | undefined,
  username: string | null | undefined,
): T[] {
  if (!items || !username) return [];
  return items.filter((item) => isAuthoredBy(item, username));
}

/** Query param that switches the gallery to the zoomed-out calendar (beta). */
export const VIEW_PARAM = "view";

/** Value written for the calendar (`?view=calendar`). */
export const VIEW_CALENDAR = "calendar";

export function isCalendarView(value: string | null | undefined): boolean {
  return (value ?? "").trim().toLowerCase() === VIEW_CALENDAR;
}
